import { z } from 'zod';

/** Valida CPF usando os dois dígitos verificadores (mesmo algoritmo do backend). */
function isValidCpf(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1+$/.test(digits)) return false;
  const calc = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += parseInt(digits[i]!, 10) * (len + 1 - i);
    const mod = (sum * 10) % 11;
    return mod === 10 ? 0 : mod;
  };
  return calc(9) === parseInt(digits[9]!, 10) && calc(10) === parseInt(digits[10]!, 10);
}

/** Valida número de CNH (11 dígitos) pelo algoritmo do DENATRAN. */
function isValidCnh(raw: string): boolean {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  let dsc = 0;
  let sum = 0;
  for (let i = 0, j = 9; i < 9; i++, j--) sum += parseInt(d[i]!, 10) * j;
  let dv1 = sum % 11;
  if (dv1 >= 10) {
    dv1 = 0;
    dsc = 2;
  }
  sum = 0;
  for (let i = 0, j = 1; i < 9; i++, j++) sum += parseInt(d[i]!, 10) * j;
  let dv2 = sum % 11;
  if (dv2 >= 10) dv2 = 0;
  else {
    dv2 -= dsc;
    if (dv2 < 0) dv2 += 11;
  }
  return dv1 === parseInt(d[9]!, 10) && dv2 === parseInt(d[10]!, 10);
}

const CNH_CATEGORIES = ['A', 'B', 'C', 'D', 'E', 'AB', 'AC', 'AD', 'AE'] as const;

export const driverFormSchema = z.object({
  name: z.string().trim().min(3, 'Informe o nome').max(120),
  cpf: z
    .string()
    .trim()
    .min(1, 'Informe o CPF')
    .refine(isValidCpf, 'CPF inválido'),
  rg: z.string().trim().max(20).optional().or(z.literal('')),
  cnh: z
    .string()
    .trim()
    .min(1, 'Informe o número da CNH')
    .refine(isValidCnh, 'Número de CNH inválido'),
  cnhCategory: z.enum(CNH_CATEGORIES),
  cnhExpiresAt: z.string().min(1, 'Informe o vencimento da CNH'),
  cnhPoints: z
    .union([z.string(), z.number()])
    .transform((v) => (v === '' || v == null ? 0 : Number(v)))
    .pipe(z.number().int().min(0).max(40))
    .optional(),
  cnhFirstIssueAt: z.string().optional().or(z.literal('')),
  birthDate: z.string().optional().or(z.literal('')),
  phone: z.string().trim().max(20).optional().or(z.literal('')),
  email: z
    .string()
    .trim()
    .max(255)
    .optional()
    .or(z.literal(''))
    .refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), 'E-mail inválido'),
  address: z.string().trim().max(255).optional().or(z.literal('')),
  city: z.string().trim().max(120).optional().or(z.literal('')),
  state: z
    .string()
    .trim()
    .toUpperCase()
    .optional()
    .or(z.literal(''))
    .refine((v) => !v || v.length === 2, 'UF deve ter 2 letras'),
  notes: z.string().trim().optional().or(z.literal('')),
  createUserAccount: z.boolean().optional(),
  accessPassword: z.string().optional().or(z.literal('')),
})
.refine(
  (data) => {
    if (!data.createUserAccount) return true;
    return !!data.email && data.email.length > 0;
  },
  { message: 'Email é obrigatório para criar acesso ao app', path: ['email'] },
)
.refine(
  (data) => {
    if (!data.createUserAccount) return true;
    return !!data.accessPassword && data.accessPassword.length >= 10;
  },
  { message: 'Senha deve ter no mínimo 10 caracteres', path: ['accessPassword'] },
);

export type DriverFormInput = z.infer<typeof driverFormSchema>;
